import React from "react";
import AppLoader from "../components/AppLoader";
import { createExploreTrial, getOAuthLogoutUrl } from "../api/authApi";
import { setExploreTrialId } from "../api/client";

const PKCE_VERIFIER_KEY = "rfm_oauth_pkce_verifier";
const TRIAL_PREFILL_PHONE_KEY = "rfm_trial_prefill_phone";
const ADD_ACCOUNT_AFTER_LOGOUT_KEY = "rfm_oauth_add_account_after_logout";
const EXPLORE_LOGIN_ERROR_KEY = "rfm_explore_login_error";

const LOGIN_HERO_SLIDES = [
  {
    kicker: "KORVRFM",
    title: "RFM Analytics Workspace",
    description:
      "See customer value, purchase behavior, and growth opportunities in one clear business view.",
    points: ["RFM Scoring", "Customer Segments", "Lifecycle Insight"],
    image: "/login-slides-rfm/rfm-analytics.svg",
  },
  {
    kicker: "KORVRFM",
    title: "Actionable Segment Intelligence",
    description:
      "Track high-value and at-risk segments, then launch focused actions to improve retention.",
    points: ["Segment Summary", "Targeted Actions", "Message Templates"],
    image: "/login-slides-rfm/rfm-segments.svg",
  },
  {
    kicker: "KORVRFM",
    title: "Revenue Trend Visibility",
    description:
      "Understand trend movement, top products, and customer timelines to guide smarter campaigns.",
    points: ["Revenue Trend", "Top Products", "Customer Timeline"],
    image: "/login-slides-rfm/rfm-revenue.svg",
  },
  {
    kicker: "KORVRFM",
    title: "Built For Any Business Domain",
    description:
      "Use one secure platform across ecommerce, services, SaaS, education, healthcare, and more.",
    points: ["Secure Access", "Org-Based Data", "Unified Dashboard"],
    image: "/login-slides-rfm/rfm-actions.svg",
  },
];

export default function StartTrial() {
  const phoneInputRef = React.useRef(null);
  const [email, setEmail] = React.useState("");
  const [phoneDigits, setPhoneDigits] = React.useState("");
  const [error, setError] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [activeHeroSlide, setActiveHeroSlide] = React.useState(0);

  const currentHeroSlide = React.useMemo(() => {
    return LOGIN_HERO_SLIDES[activeHeroSlide] || LOGIN_HERO_SLIDES[0];
  }, [activeHeroSlide]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailFromQuery = (params.get("email") || params.get("e") || "").trim().toLowerCase();
    if (emailFromQuery) {
      setEmail(emailFromQuery);
    }
    const pendingExploreError = (window.sessionStorage.getItem(EXPLORE_LOGIN_ERROR_KEY) || "").trim();
    if (pendingExploreError) {
      setError(pendingExploreError);
      window.sessionStorage.removeItem(EXPLORE_LOGIN_ERROR_KEY);
    }
  }, []);

  React.useEffect(() => {
    if (LOGIN_HERO_SLIDES.length <= 1) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveHeroSlide((current) => (current + 1) % LOGIN_HERO_SLIDES.length);
    }, 5200);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  async function onStartTrial(event) {
    event.preventDefault();
    const normalizedEmail = (email || "").trim().toLowerCase();
    const digits = String(phoneDigits || "").replace(/\D/g, "").slice(0, 10);
    if (!digits) {
      setError("Phone number is required.");
      return;
    }
    if (digits.length !== 10) {
      setError("Phone is not valid. Enter exactly 10 digits.");
      return;
    }

    if (!normalizedEmail || !/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");

      // Format phone before API call
      const formattedPhone = `+91${digits}`;

      console.log("[StartTrial] Creating explore trial for:", normalizedEmail);
      const exploreTrialResult = await createExploreTrial(normalizedEmail, formattedPhone);
      console.log("[StartTrial] Explore trial created:", exploreTrialResult);

      if (exploreTrialResult && exploreTrialResult.id) {
        setExploreTrialId(exploreTrialResult.id);
      }
      // Save normalized phone as +91<10digits> for use after OAuth
      window.sessionStorage.setItem(TRIAL_PREFILL_PHONE_KEY, formattedPhone);
      window.sessionStorage.setItem(ADD_ACCOUNT_AFTER_LOGOUT_KEY, "1");
      window.sessionStorage.removeItem("rfm_oauth_state");
      window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);

      const redirectUri = `${window.location.origin}`;

      const data = await getOAuthLogoutUrl(redirectUri, {
      });

      if (!data?.logout_url) {
        throw new Error("Failed to get login URL.");
      }

      const state = (data?.state || "").trim();
      if (state) {
        window.sessionStorage.setItem("rfm_oauth_state", state);
      }

      window.location.assign(data.logout_url);
    } catch (err) {
      console.error("[StartTrial] Error:", err?.message || err);
      setError(`Error starting trial: ${err?.response?.data?.detail || err?.message || "Unknown error"}`);
    } finally {
      setIsSubmitting(false);
    }
  }

  function onPhoneChange(event) {
    const nextValue = String(event.target.value || "")
      .replace(/\D/g, "")
      .slice(0, 10);
    setPhoneDigits(nextValue);
    if (error) {
      setError("");
    }
  }

  return (
    <div className="start-trial-screen">
      <div className="login-card enterprise start-trial-shell" role="dialog" aria-labelledby="start-trial-title">
        <aside className="login-hero-side" aria-hidden="true">
          <div
            className="login-hero-slider-track"
            style={{ transform: `translateX(-${activeHeroSlide * 100}%)` }}
          >
            {LOGIN_HERO_SLIDES.map((slide) => (
              <div
                key={slide.image}
                className="login-hero-slide"
                style={{ backgroundImage: `url(${slide.image})` }}
              />
            ))}
          </div>
          <div className="login-hero-overlay" />
          <div className="login-hero-grid" />
          <div className="login-hero-copy">
            <h2>{currentHeroSlide.title}</h2>
            <p>{currentHeroSlide.description}</p>
            <div className="login-hero-points">
              {(currentHeroSlide.points || []).map((point) => (
                <span key={point} className="login-hero-point">{point}</span>
              ))}
            </div>
          </div>
          <div className="login-hero-dots">
            {LOGIN_HERO_SLIDES.map((slide, index) => (
              <span
                key={slide.image}
                className={`login-hero-dot ${index === activeHeroSlide ? "active" : ""}`}
              />
            ))}
          </div>
        </aside>

        <form onSubmit={onStartTrial} className="login-form-side start-trial-form-side">
          <h1 id="start-trial-title">KorvRFM</h1>

          <label className="start-trial-label">Phone number</label>
          <div className="start-trial-phone-row">
            <span className="start-trial-prefix" aria-hidden="true">+91</span>
            <input
              ref={phoneInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="tel-national"
              aria-label="Phone number"
              value={phoneDigits}
              onChange={onPhoneChange}
              placeholder="9876543210"
              disabled={isSubmitting}
              maxLength={10}
              className="start-trial-input start-trial-phone-input"
            />
          </div>

          <label className="start-trial-label">Email</label>
          <div className="start-trial-phone-row">
            <span className="start-trial-prefix" aria-hidden="true">@</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={isSubmitting}
              autoComplete="email"
              className="start-trial-input"
            />
          </div>
          <div className="start-trial-field-note emphasis">
            <span className="start-trial-warning-mark" aria-hidden="true">!</span>
            <span><strong>Important:</strong> Use this exact email address for Google sign-in.</span>
          </div>

          {error ? <p className="start-trial-error" role="alert">{error}</p> : null}

          <button type="submit" disabled={isSubmitting} className="start-trial-submit">
            {isSubmitting ? (
              <span className="btn-inline-loading">
                <span className="btn-spinner" />
                Preparing secure sign-in...
              </span>
            ) : (
              "Continue With Google"
            )}
          </button>

          {isSubmitting ? (
            <div className="start-trial-loader-wrap">
              <AppLoader
                title="KorvRFM"
                message="Preparing the secure Google sign-in flow..."
                variant="compact"
              />
            </div>
          ) : null}
        </form>
      </div>
    </div>
  );
}
